import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const fmt = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.005) return "";
  return Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmt0 = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABEL = {
  Asset: "Assets",
  Liability: "Liabilities",
  Equity: "Equity",
  Income: "Income",
  Expense: "Expenses",
};

/* Split the flat rows into their type sections, keeping the server order. */
function groupRows(rows) {
  const out = [];
  let current = null;
  for (const r of rows) {
    // Cost of Goods Sold rides in as type "Expense" with a COGS subtype; give
    // it its own visual heading so it reads like the P&L.
    const heading =
      r.type === "Expense" && r.subType === "Cost of Goods Sold"
        ? "Cost of Goods Sold"
        : TYPE_LABEL[r.type] || r.type;
    if (!current || current.heading !== heading) {
      current = { heading, rows: [] };
      out.push(current);
    }
    current.rows.push(r);
  }
  return out;
}

export default function TrialBalance() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    if (!asOf) { setError("Pick a date"); return; }
    setLoading(true); setError(null);
    api.get("/accounting/trial-balance", { params: { asOf } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build Trial Balance"))
      .finally(() => setLoading(false));
  }, [asOf]);

  const sections = data ? groupRows(data.rows) : [];

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Trial Balance</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Every account with a balance, in its Debit or Credit column, as of a date. Cumulative since
        the books began. Net income is not yet closed to equity (pre-closing trial balance).
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="As of" InputLabelProps={{ shrink: true }}
            value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Trial Balance</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>As of {data.asOf}</Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ maxWidth: 760, mx: "auto" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Debit</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Credit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sections.map((sec) => (
                  <React.Fragment key={sec.heading}>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0, pt: 1.5, pb: 0.5, fontWeight: 700, fontSize: 15 }}>
                        {sec.heading}
                      </TableCell>
                    </TableRow>
                    {sec.rows.map((r, i) => (
                      <TableRow key={`${sec.heading}-${r.account}-${i}`}>
                        <TableCell sx={{ pl: 3, border: 0, py: 0.4 }}>{r.account}</TableCell>
                        <TableCell align="right" sx={{ border: 0, py: 0.4, fontVariantNumeric: "tabular-nums" }}>{fmt(r.debit)}</TableCell>
                        <TableCell align="right" sx={{ border: 0, py: 0.4, fontVariantNumeric: "tabular-nums" }}>{fmt(r.credit)}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}

                {/* Totals */}
                <TableRow>
                  <TableCell sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, fontSize: 15 }}>
                    Totals
                  </TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                    {fmt0(data.totalDebit)}
                  </TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                    {fmt0(data.totalCredit)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {data.balanced ? (
            <Alert severity="success" sx={{ mt: 2, maxWidth: 760, mx: "auto" }}>
              In balance — total debits equal total credits.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 2, maxWidth: 760, mx: "auto" }}>
              <strong>Out of balance by {fmt0(Math.abs(data.difference))}</strong>{" "}
              ({data.difference > 0 ? "debits exceed credits" : "credits exceed debits"}).
              This is expected until opening balances and inventory value are entered — it is the
              same gap shown on the Balance Sheet. This number is our target: closing it is the goal
              of the next steps.
            </Alert>
          )}

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Built from the same sources as your Profit &amp; Loss and Balance Sheet — sale/purchase
            invoices, Daily Cash, the party ledger, and journal entries — so it always agrees with them.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
